import { Check, Loader2, Sparkles } from "lucide-react";
import { cn } from "../lib/utils";
import {
  gluceraBody,
  gluceraLoaderEyebrow,
  gluceraLoaderHeadline,
  gluceraLoaderPercentBadge,
  gluceraLoaderProgressMeta,
  gluceraLoaderStepStatus,
  gluceraLoaderStepTitle,
} from "../utils/gluceraTypography";

const ANALYZE_STEPS = [
  { max: 30, title: "Reading your plate" },
  { max: 70, title: "Computing glycemic load" },
  { max: 90, title: "Comparing to your history" },
  { max: 100, title: "Predicting your curve" },
] as const;

type MealAnalyzingProgressOverlayProps = {
  uploadProgress: number;
};

export function MealAnalyzingProgressOverlay({
  uploadProgress,
}: MealAnalyzingProgressOverlayProps) {
  const pct = Math.round(uploadProgress);

  const activeStepIndex = ANALYZE_STEPS.findIndex((step, i) => {
    const prev = i === 0 ? 0 : ANALYZE_STEPS[i - 1].max;
    return uploadProgress >= prev && uploadProgress < step.max;
  });

  const headlineIdx =
    activeStepIndex >= 0
      ? activeStepIndex
      : pct >= 100
        ? ANALYZE_STEPS.length - 1
        : 0;
  const headline = ANALYZE_STEPS[headlineIdx].title;

  return (
    <div className="fixed inset-0 z-[9999] overflow-y-auto overscroll-contain">
      <div
        className="fixed inset-0 bg-black/55 backdrop-blur-md"
        style={{ backdropFilter: "blur(14px)" }}
        aria-hidden
      />

      <div className="relative flex min-h-full items-center justify-center p-4">
        <div
          className="relative z-10 w-full max-w-[min(400px,100%)]"
          role="dialog"
          aria-labelledby="meal-analyze-title"
          aria-busy="true"
        >
        <div className="overflow-hidden rounded-[1.75rem] border border-border/80 bg-card shadow-2xl ring-1 ring-black/[0.04] dark:ring-white/[0.06]">
          {/* Header band */}
          <div className="relative border-b border-border/70 bg-muted/25 px-6 pb-6 pt-6 dark:bg-muted/15">
            <div
              className="pointer-events-none absolute inset-0 opacity-[0.35] dark:opacity-20"
              style={{
                backgroundImage:
                  "radial-gradient(hsl(var(--foreground) / 0.08) 1px, transparent 1px)",
                backgroundSize: "12px 12px",
              }}
              aria-hidden
            />

            <div className="relative flex items-start justify-between gap-4">
              <span className={cn("inline-flex items-center gap-2 rounded-full border border-border/80 bg-card px-3 py-1.5 shadow-sm", gluceraLoaderEyebrow)}>
                <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-amber-500" aria-hidden />
                Analyzing your meal
              </span>
              <span className={cn("rounded-xl bg-foreground px-3 py-1.5", gluceraLoaderPercentBadge)}>
                {pct}%
              </span>
            </div>

            <div className="relative mt-6 flex flex-col items-center text-center">
              <div className="relative mb-5">
                <div className="flex h-[88px] w-[88px] items-center justify-center rounded-full bg-card shadow-md ring-1 ring-border/80">
                  <Loader2
                    className="h-10 w-10 animate-spin text-amber-600 dark:text-amber-400"
                    strokeWidth={2}
                    aria-hidden
                  />
                </div>
                <div
                  className="absolute inset-0 animate-ping rounded-full border-2 border-amber-400/25 opacity-40"
                  aria-hidden
                />
              </div>

              <h2
                id="meal-analyze-title"
                className={cn("max-w-[280px]", gluceraLoaderHeadline)}
              >
                {headline}
              </h2>
              <p className={cn("mt-2 max-w-[300px]", gluceraBody)}>
                Please wait while we process your photo.
              </p>
            </div>

            <div className="relative mt-6">
              <div className="h-2.5 w-full overflow-hidden rounded-full bg-muted/80">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-amber-500 via-amber-600 to-emerald-600 transition-all duration-500 ease-out"
                  style={{ width: `${pct}%` }}
                />
              </div>
              <div className={cn("mt-2 flex items-center justify-between", gluceraLoaderProgressMeta)}>
                <span>Progress</span>
                <span className="inline-flex items-center gap-1.5 tabular-nums">
                  <Sparkles className="h-3.5 w-3.5 text-amber-600/80" aria-hidden />
                  Step {Math.min(headlineIdx + 1, ANALYZE_STEPS.length)} of {ANALYZE_STEPS.length}
                </span>
              </div>
            </div>
          </div>

          {/* Steps */}
          <ul className="space-y-3 px-5 py-5 sm:px-6 sm:py-6">
            {ANALYZE_STEPS.map((step, i) => {
              const prev = i === 0 ? 0 : ANALYZE_STEPS[i - 1].max;
              const active = uploadProgress >= prev && uploadProgress < step.max;
              const done = uploadProgress >= step.max;
              const isLast = i === ANALYZE_STEPS.length - 1;

              return (
                <li key={step.title} className="relative">
                  {!isLast && (
                    <div
                      className={cn(
                        "absolute left-[38px] top-12 h-[calc(100%+0.75rem)] w-0.5 -translate-x-1/2",
                        done ? "bg-emerald-600/40" : "bg-border",
                      )}
                      aria-hidden
                    />
                  )}
                  <div
                    className={cn(
                      "relative flex items-center gap-4 rounded-2xl border px-4 py-4 transition-all duration-300",
                      active &&
                        "border-amber-200/90 bg-amber-50/50 shadow-sm dark:border-amber-800/40 dark:bg-amber-950/20",
                      done &&
                        "border-emerald-200/80 bg-emerald-50/40 dark:border-emerald-900/40 dark:bg-emerald-950/15",
                      !active &&
                        !done &&
                        "border-border/60 bg-muted/20 opacity-50",
                    )}
                  >
                    <div className="relative z-[1] shrink-0">
                      {done ? (
                        <div className="flex h-11 w-11 items-center justify-center rounded-full bg-emerald-700 shadow-sm dark:bg-emerald-600">
                          <Check className="h-5 w-5 text-white" strokeWidth={2.5} aria-hidden />
                        </div>
                      ) : active ? (
                        <div className="flex h-11 w-11 items-center justify-center rounded-full border-2 border-amber-300/80 bg-card shadow-sm dark:border-amber-700/50">
                          <Loader2
                            className="h-5 w-5 animate-spin text-amber-600 dark:text-amber-400"
                            strokeWidth={2}
                            aria-hidden
                          />
                        </div>
                      ) : (
                        <div
                          className="h-11 w-11 rounded-full border-2 border-dashed border-border bg-card"
                          aria-hidden
                        />
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p
                        className={cn(
                          gluceraLoaderStepTitle,
                          active || done ? "text-foreground" : "text-muted-foreground",
                        )}
                      >
                        {step.title}
                      </p>
                      <p
                        className={cn(
                          "mt-1",
                          gluceraLoaderStepStatus,
                          done && "text-emerald-700 dark:text-emerald-400",
                          active && "text-amber-700 dark:text-amber-400",
                          !done && !active && "text-muted-foreground",
                        )}
                      >
                        {done ? "Complete" : active ? "In progress" : "Waiting"}
                      </p>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
        </div>
      </div>
    </div>
  );
}

export default MealAnalyzingProgressOverlay;
